import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeacher } from '@/context/TeacherContext';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Calendar, Plus, Edit2, Trash2, Users, CheckCircle2, AlertCircle, XCircle, Clock, Loader2, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { getAuthToken } from '@/utils/auth';
import * as chapterPlannerAPI from '@/api/chapterPlanner';
import { trackEvent, attachUserProps } from '@/analytics/analytics';

export default function ChapterPlannerList() {
  const navigate = useNavigate();
  const { teacherTheme, isDark } = useTeacher();
  const { toast } = useToast();
  
  const [plans, setPlans] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [assignPlan, setAssignPlan] = useState(null);
  const [selectedDivisionIds, setSelectedDivisionIds] = useState([]);
  const [assigningClasses, setAssigningClasses] = useState(false);
  const [viewingPlan, setViewingPlan] = useState(null);
  const [viewingDivision, setViewingDivision] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Fetch plans and classes
  useEffect(() => {
    fetchPlans();
    fetchClasses();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      console.log('=== fetchPlans called ===');
      console.log('Calling chapterPlannerAPI.getPlans()...');
      
      const data = await chapterPlannerAPI.getPlans();
      
      console.log('=== Raw API Response ===');
      console.log('Response type:', typeof data);
      console.log('Is array:', Array.isArray(data));
      console.log('Full response:', JSON.stringify(data, null, 2));
      
      // Backend returns array directly (Response(serializer.data) where many=True)
      // Check if it's already an array or wrapped in an object
      let plansArray = [];
      if (Array.isArray(data)) {
        plansArray = data;
        console.log('Response is array, using directly');
      } else if (data && Array.isArray(data.plans)) {
        plansArray = data.plans;
        console.log('Response has plans array property');
      } else if (data && typeof data === 'object') {
        // If it's an object, try to find any array property
        plansArray = Object.values(data).find(val => Array.isArray(val)) || [];
        console.log('Response is object, searching for array property');
      } else {
        console.warn('Unexpected response format:', data);
      }
      
      console.log('=== Processed Plans Array ===');
      console.log('Plans array length:', plansArray.length);
      console.log('Plans array:', plansArray);
      
      if (plansArray.length > 0) {
        console.log('First plan sample:', JSON.stringify(plansArray[0], null, 2));
        console.log('First plan structure check:');
        console.log('  - plan.chapter:', plansArray[0].chapter);
        console.log('  - plan.chapter_name:', plansArray[0].chapter_name);
        console.log('  - plan.subject:', plansArray[0].subject);
        console.log('  - plan.subject_name:', plansArray[0].subject_name);
        console.log('  - plan.grade:', plansArray[0].grade);
        console.log('  - plan.grade_name:', plansArray[0].grade_name);
        console.log('  - plan.periods:', plansArray[0].periods);
        console.log('  - plan.periods length:', plansArray[0].periods?.length);
      }
      
      setPlans(plansArray);
      
      if (plansArray.length === 0) {
        console.warn('⚠️ No plans found in response');
      } else {
        console.log(`✅ Successfully set ${plansArray.length} plans to state`);
      }
    } catch (err) {
      console.error('=== Error in fetchPlans ===');
      console.error('Error object:', err);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data);
      console.error('Error response status:', err.response?.status);
      console.error('Error response headers:', err.response?.headers);
      console.error('Full error:', JSON.stringify(err, null, 2));
      
      toast({
        title: "Error",
        description: err?.response?.data?.error || err?.message || "Failed to load chapter plans.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      console.log('=== fetchPlans completed ===');
    }
  };

  const fetchClasses = async () => {
    try {
      const token = getAuthToken('teacher');
      if (!token) return;
      
      const res = await axios.get(apiUrl('v1/teachers/classes/'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const apiClasses = res?.data?.classes || [];
      console.log('=== Fetched Classes ===');
      console.log('Classes count:', apiClasses.length);
      if (apiClasses.length > 0) {
        console.log('First class sample:', apiClasses[0]);
      }
      setClasses(apiClasses);
    } catch (err) {
      console.error('Failed to load classes', err);
    }
  };

  const handleAssignClasses = async (plan) => {
    try {
      // Fetch full plan detail to get current divisions
      const planDetail = await chapterPlannerAPI.getPlan(plan.id);
      setAssignPlan(planDetail);
      // Pre-select currently assigned division IDs
      const currentDivisionIds = (planDetail.divisions || []).map(div => div.id || div);
      setSelectedDivisionIds(currentDivisionIds);
      try { trackEvent('teacher_open_assign_plan_dialog', attachUserProps({ plan_id: plan.id })); } catch(e) {}
    } catch (err) {
      console.error('Failed to load plan', err);
      toast({
        title: "Error",
        description: "Failed to load plan details.",
        variant: "destructive",
      });
    }
  };

  const handleCloseAssignClasses = () => {
    setAssignPlan(null);
    setSelectedDivisionIds([]);
  };

  const handleToggleDivision = (divisionId) => {
    setSelectedDivisionIds(prev => {
      if (prev.includes(divisionId)) {
        return prev.filter(id => id !== divisionId);
      } else {
        return [...prev, divisionId];
      }
    });
  };

  const handleSaveAssignedClasses = async () => {
    if (!assignPlan) return;
    
    if (selectedDivisionIds.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one class.",
        variant: "destructive",
      });
      return;
    }

    setAssigningClasses(true);
    try {
      // TODO: Update backend API to support division_ids
      // For now, we'll use updatePlan with division_ids
      const divisionIds = selectedDivisionIds.map(id => parseInt(id)).filter(id => !isNaN(id));
      
      await chapterPlannerAPI.updatePlan(assignPlan.id, {
        division_ids: divisionIds,
      });
      
      // Refresh plans list
      await fetchPlans();
      
      handleCloseAssignClasses();
      try { trackEvent('teacher_plan_assigned', attachUserProps({ plan_id: assignPlan.id, assigned_division_count: divisionIds.length })); } catch(e) {}
      toast({
        title: "Success",
        description: "Plan assigned to classes successfully.",
      });
    } catch (err) {
      console.error('Failed to assign classes', err);
      toast({
        title: "Error",
        description: err?.response?.data?.error || "Failed to assign classes.",
        variant: "destructive",
      });
    } finally {
      setAssigningClasses(false);
    }
  };

  const handleViewPlan = async (plan, division = null) => {
    try {
      const planDetail = await chapterPlannerAPI.getPlan(plan.id);
      setViewingPlan(planDetail);
      // If no division specified and plan has divisions, default to first division
      if (!division && planDetail.divisions && planDetail.divisions.length > 0) {
        setViewingDivision({ id: planDetail.divisions[0].id, full_name: planDetail.divisions[0].division_name });
      } else {
        setViewingDivision(division);
      }
    } catch (err) {
      console.error('Failed to load plan', err);
      toast({
        title: "Error",
        description: "Failed to load plan details.",
        variant: "destructive",
      });
    }
  };

  const handleUpdatePeriodStatus = async (planId, periodNumber, status, divisionId = null) => {
    setUpdatingStatus(true);
    try {
      await chapterPlannerAPI.updatePeriodStatus(planId, periodNumber, status, [], divisionId);
      
      // Refresh plans
      await fetchPlans();
      
      // Refresh viewing plan if open
      if (viewingPlan && viewingPlan.id === planId) {
        const updatedPlan = await chapterPlannerAPI.getPlan(planId);
        setViewingPlan(updatedPlan);
      }
      
      toast({
        title: "Status Updated",
        description: `Period ${periodNumber} marked as ${status}.`,
      });
    } catch (err) {
      console.error('Failed to update status', err);
      toast({
        title: "Error",
        description: err?.response?.data?.error || "Failed to update status.",
        variant: "destructive",
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    try {
      await chapterPlannerAPI.deletePlan(planId);
      await fetchPlans();
      toast({
        title: "Deleted",
        description: "Plan deleted successfully.",
      });
    } catch (err) {
      console.error('Failed to delete plan', err);
      toast({
        title: "Error",
        description: err?.response?.data?.error || "Failed to delete plan.",
        variant: "destructive",
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'partial':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'skipped':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/10 text-green-600 border-green-500/20';
      case 'partial':
        return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
      case 'skipped':
        return 'bg-red-500/10 text-red-600 border-red-500/20';
      default:
        return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
    }
  };

  // Debug: Log plans and filtered plans
  useEffect(() => {
    console.log('=== Plans State Update ===');
    console.log('Total plans in state:', plans.length);
    console.log('Search term:', searchTerm);
  }, [plans, searchTerm]);

  const filteredPlans = plans.filter(plan => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    // Check both nested object format and flat format
    const chapterName = plan.chapter?.chapter_name || plan.chapter_name || '';
    const subjectName = plan.subject?.subject_name || plan.subject_name || '';
    const gradeName = plan.grade?.grade_name || plan.grade_name || '';
    
    const matches = chapterName.toLowerCase().includes(searchLower) ||
           subjectName.toLowerCase().includes(searchLower) ||
           gradeName.toLowerCase().includes(searchLower);
    
    if (!matches) {
      console.log('Plan filtered out:', {
        id: plan.id,
        chapterName,
        subjectName,
        gradeName,
        searchTerm
      });
    }
    
    return matches;
  });

  useEffect(() => {
    console.log('=== Filtered Plans ===');
    console.log('Filtered plans count:', filteredPlans.length);
    if (filteredPlans.length > 0) {
      console.log('First filtered plan:', filteredPlans[0]);
    }
  }, [filteredPlans]);

  // Get filtered classes for assignment (matching plan's subject and grade)
  const getFilteredClassesForPlan = (plan) => {
    if (!plan) return [];
    const planSubjectId = plan.subject?.id || plan.subject_id;
    const planGradeId = plan.grade?.id || plan.grade_id;
    
    return classes.filter(cls => 
      cls.subject_id === planSubjectId && cls.grade_id === planGradeId
    );
  };

  return (
    <div className={cn("flex flex-col h-full overflow-hidden", isDark ? "bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--card))] to-[hsl(var(--background))]" : "bg-gradient-to-br from-[#F8F5F0] via-[#F5F1EA] to-[#F2EBE0]")}>
      <div className="flex-shrink-0 p-6 pb-4">
        <div className="max-w-7xl mx-auto w-full space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/teacher/ai-tools')}
                className={cn("rounded-xl", isDark ? "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/60" : "text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EBE3]/60")}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className={cn("text-4xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>
                  Chapter Planner
                </h1>
                <p className={cn("mt-1 text-sm font-normal", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#8B7B6B]")}>
                  Manage and assign your teaching plans
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/teacher/ai-tools/chapter-planner/create')}
              className={cn(isDark ? "bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" : "bg-[#C9A961] hover:bg-[#B89951]")}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create New Plan
            </Button>
          </div>

          {/* Search */}
          <div className="mb-4">
            <Input
              placeholder="Search plans by chapter, subject, or grade..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={cn("max-w-md", isDark ? "bg-[hsl(var(--card))]" : "bg-white")}
            />
          </div>
        </div>
      </div>

      {/* Plans List */}
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : filteredPlans.length === 0 ? (
            <Card className={cn(isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
              <CardContent className="p-12 text-center">
                <Calendar className={cn("w-16 h-16 mx-auto mb-4", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
                <h3 className={cn("text-xl font-bold mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                  No plans found
                </h3>
                <p className={cn("mb-4", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                  {searchTerm ? 'Try a different search term.' : 'Create your first teaching plan to get started.'}
                </p>
                {!searchTerm && (
                  <Button
                    onClick={() => navigate('/teacher/ai-tools/chapter-planner/create')}
                    className={cn(isDark ? "bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" : "bg-[#C9A961] hover:bg-[#B89951]")}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Plan
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredPlans.map((plan) => (
                <Card key={plan.id} className={cn(
                  isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]"
                )}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className={cn("text-xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                            {plan.chapter?.chapter_name || plan.chapter_name || 'Untitled Chapter'}
                          </h3>
                          <Badge variant="outline">
                            {plan.grade?.grade_name || plan.grade_name} - {plan.subject?.subject_name || plan.subject_name}
                          </Badge>
                        </div>
                        <div className={cn("flex items-center gap-6 text-sm mb-4 flex-wrap", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                          <p>{plan.total_periods} periods × {plan.period_duration} minutes</p>
                          <p>Generated: {new Date(plan.generated_at).toLocaleDateString()}</p>
                          {plan.divisions && plan.divisions.length > 0 && (
                            <p className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              Assigned to {plan.divisions.length} class{plan.divisions.length !== 1 ? 'es' : ''}
                            </p>
                          )}
                        </div>
                        {plan.periods && plan.periods.length > 0 && plan.divisions && plan.divisions.length > 0 && (
                          <div className="mt-4 pt-4 border-t">
                            <p className={cn("text-xs font-semibold uppercase tracking-wide mb-3", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                              Period Status by Division
                            </p>
                            <div className="overflow-x-auto">
                              <div className="flex gap-4 pb-2" style={{ minWidth: 'min-content' }}>
                                {plan.divisions.map((division) => {
                                  const subjectName = plan.subject?.subject_name || plan.subject_name || '';
                                  const gradeName = division.grade?.grade_name || plan.grade?.grade_name || plan.grade_name || '';
                                  const divisionName = division.division_name || '';
                                  const displayName = `${subjectName} - ${gradeName} ${divisionName}`.trim();
                                  
                                  return (
                                    <Card 
                                      key={division.id} 
                                      className={cn("flex-shrink-0 w-80", isDark ? "bg-[hsl(var(--card-border))]/30 border-[hsl(var(--card-border))]" : "bg-[#F8F5F0] border-[#E0D4BF]")}
                                    >
                                      <CardHeader className="pb-3">
                                        <CardTitle className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                                          {displayName}
                                        </CardTitle>
                                      </CardHeader>
                                      <CardContent className="space-y-2">
                                        {plan.periods.map((period) => {
                                          // Find division-specific status
                                          const divStatus = period.division_statuses?.find(
                                            ds => ds.division_id === division.id
                                          );
                                          const status = divStatus?.status || period.status || 'pending';
                                          return (
                                            <div 
                                              key={`${period.period_number}-${division.id}`}
                                              className={cn("flex items-center justify-between p-2 rounded-lg", isDark ? "bg-[hsl(var(--background))]" : "bg-white")}
                                            >
                                              <div className="flex items-center gap-2">
                                                {getStatusIcon(status)}
                                                <span className={cn("text-sm font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                                                  Period {period.period_number}
                                                </span>
                                                <Badge 
                                                  className={cn("text-xs", getStatusColor(status))}
                                                >
                                                  {status.charAt(0).toUpperCase() + status.slice(1)}
                                                </Badge>
                                              </div>
                                              <div className="flex gap-1">
                                                <Button
                                                  size="sm"
                                                  variant={status === 'completed' ? 'default' : 'outline'}
                                                  onClick={() => handleUpdatePeriodStatus(plan.id, period.period_number, 'completed', division.id)}
                                                  disabled={updatingStatus}
                                                  className="h-7 px-2 text-xs"
                                                  title="Mark as Completed"
                                                >
                                                  <CheckCircle2 className="w-3 h-3" />
                                                </Button>
                                                <Button
                                                  size="sm"
                                                  variant={status === 'partial' ? 'default' : 'outline'}
                                                  onClick={() => handleUpdatePeriodStatus(plan.id, period.period_number, 'partial', division.id)}
                                                  disabled={updatingStatus}
                                                  className="h-7 px-2 text-xs"
                                                  title="Mark as Partial"
                                                >
                                                  <AlertCircle className="w-3 h-3" />
                                                </Button>
                                                <Button
                                                  size="sm"
                                                  variant={status === 'skipped' ? 'default' : 'outline'}
                                                  onClick={() => handleUpdatePeriodStatus(plan.id, period.period_number, 'skipped', division.id)}
                                                  disabled={updatingStatus}
                                                  className="h-7 px-2 text-xs"
                                                  title="Mark as Skipped"
                                                >
                                                  <XCircle className="w-3 h-3" />
                                                </Button>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </CardContent>
                                    </Card>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}
                        {plan.periods && plan.periods.length > 0 && (!plan.divisions || plan.divisions.length === 0) && (
                          <div className="flex flex-wrap gap-2">
                            {plan.periods.map((period) => (
                              <Badge key={period.period_number} className={getStatusColor(period.status)}>
                                {getStatusIcon(period.status)}
                                <span className="ml-1">Period {period.period_number}</span>
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/teacher/ai-tools/chapter-planner/${plan.id}`)}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAssignClasses(plan)}
                        >
                          <Users className="w-4 h-4 mr-1" />
                          Assign Classes
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeletePlan(plan.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Assign Classes Dialog */}
      <Dialog open={!!assignPlan} onOpenChange={handleCloseAssignClasses}>
        <DialogContent className={cn("max-w-2xl", isDark ? "bg-[hsl(var(--card))]" : "bg-white")}>
          <DialogHeader>
            <DialogTitle className={cn(isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
              Assign Plan to Classes
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className={cn("text-sm", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
              Select classes (Grade-Division) to assign this plan to:
            </p>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {assignPlan && (() => {
                // Filter classes to only show those matching the plan's subject and grade
                // Handle both nested object format and flat format
                const planSubjectId = assignPlan.subject?.id || assignPlan.subject_id || assignPlan.subject;
                const planGradeId = assignPlan.grade?.id || assignPlan.grade_id || assignPlan.grade;
                
                console.log('=== Assign Classes Debug ===');
                console.log('Plan:', assignPlan);
                console.log('Plan subject:', assignPlan.subject);
                console.log('Plan subject_id:', assignPlan.subject_id);
                console.log('Plan grade:', assignPlan.grade);
                console.log('Plan grade_id:', assignPlan.grade_id);
                console.log('Resolved planSubjectId:', planSubjectId, typeof planSubjectId);
                console.log('Resolved planGradeId:', planGradeId, typeof planGradeId);
                console.log('Total classes available:', classes.length);
                if (classes.length > 0) {
                  console.log('Sample classes:', classes.slice(0, 3).map(c => ({ 
                    id: c.id, 
                    subject_id: c.subject_id, 
                    grade_id: c.grade_id, 
                    full_name: c.full_name 
                  })));
                }
                
                // Convert IDs to numbers for consistent comparison
                const normalizeId = (id) => {
                  if (id == null || id === '') return null;
                  const num = typeof id === 'string' ? parseInt(id, 10) : id;
                  return isNaN(num) ? null : num;
                };
                
                const normalizedPlanSubjectId = normalizeId(planSubjectId);
                const normalizedPlanGradeId = normalizeId(planGradeId);
                
                console.log('Normalized planSubjectId:', normalizedPlanSubjectId);
                console.log('Normalized planGradeId:', normalizedPlanGradeId);
                
                if (!normalizedPlanSubjectId || !normalizedPlanGradeId) {
                  console.warn('Missing plan subject or grade ID - cannot filter classes');
                }
                
                const filteredClasses = classes.filter(cls => {
                  const clsSubjectId = normalizeId(cls.subject_id);
                  const clsGradeId = normalizeId(cls.grade_id);
                  
                  const matches = clsSubjectId === normalizedPlanSubjectId && clsGradeId === normalizedPlanGradeId;
                  if (matches) {
                    console.log('✓ Match found:', cls.full_name, { clsSubjectId, clsGradeId, planSubId: normalizedPlanSubjectId, planGrId: normalizedPlanGradeId });
                  }
                  return matches;
                });
                
                console.log('Filtered classes count:', filteredClasses.length);
                
                return filteredClasses.length > 0 ? (
                  filteredClasses.map((cls) => {
                    const divisionId = cls.division_id;
                    return (
                      <div 
                        key={cls.id} 
                        className={cn("flex items-center space-x-2 p-2 rounded hover:bg-opacity-50 cursor-pointer", 
                          isDark ? "hover:bg-[hsl(var(--card-border))]" : "hover:bg-[#E0D4BF]"
                        )}
                        onClick={() => handleToggleDivision(divisionId)}
                      >
                        <Checkbox
                          id={`division-${cls.id}`}
                          checked={selectedDivisionIds.includes(divisionId)}
                          onCheckedChange={() => handleToggleDivision(divisionId)}
                          className={cn(isDark ? "border-[hsl(var(--card-border))]" : "border-[#E0D4BF]")}
                        />
                        <label
                          htmlFor={`division-${cls.id}`}
                          className={cn("text-sm font-medium cursor-pointer flex-1", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}
                        >
                          {cls.full_name || `${cls.subject_name} - Grade ${cls.grade_name} ${cls.division_name}`}
                        </label>
                      </div>
                    );
                  })
                ) : (
                  <p className={cn("text-sm text-center py-4", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                    No classes available for this plan's grade and subject.
                  </p>
                );
              })()}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseAssignClasses}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveAssignedClasses}
              disabled={assigningClasses || selectedDivisionIds.length === 0}
            >
              {assigningClasses ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                'Assign'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Plan Dialog with Period Status */}
      <Dialog open={!!viewingPlan} onOpenChange={() => {
        setViewingPlan(null);
        setViewingDivision(null);
      }}>
        <DialogContent className={cn("max-w-4xl max-h-[90vh] overflow-y-auto", isDark ? "bg-[hsl(var(--card))]" : "bg-white")}>
          <DialogHeader>
            <DialogTitle className={cn(isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
              {viewingPlan?.chapter?.chapter_name || viewingPlan?.chapter_name} - Period Status
            </DialogTitle>
          </DialogHeader>
          
          {/* Division Selector */}
          {viewingPlan?.divisions && viewingPlan.divisions.length > 0 && (
            <div className="mb-4 pb-4 border-b">
              <Label className={cn("text-sm font-medium mb-2 block", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                Select Division:
              </Label>
              <div className="flex flex-wrap gap-2">
                {viewingPlan.divisions.map((division) => {
                  const isSelected = viewingDivision?.id === division.id;
                  const subjectName = viewingPlan.subject?.subject_name || viewingPlan.subject_name || '';
                  const gradeName = division.grade?.grade_name || viewingPlan.grade?.grade_name || viewingPlan.grade_name || '';
                  const divisionName = division.division_name || '';
                  const displayName = `${subjectName} - ${gradeName} ${divisionName}`.trim();
                  
                  return (
                    <Button
                      key={division.id}
                      size="sm"
                      variant={isSelected ? "default" : "outline"}
                      onClick={() => setViewingDivision({ id: division.id, full_name: displayName })}
                      className={cn(
                        isSelected 
                          ? (isDark ? "bg-[hsl(var(--primary))]" : "bg-[#C9A961]")
                          : ""
                      )}
                    >
                      {displayName}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
          
          {!viewingDivision && viewingPlan?.divisions && viewingPlan.divisions.length > 0 ? (
            <p className={cn("text-center py-8", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
              Please select a division to view period statuses
            </p>
          ) : (
            <div className="space-y-4 py-4">
              {viewingPlan?.periods && viewingPlan.periods.map((period) => {
                // Get division-specific status if division is selected
                const divStatus = viewingDivision && period.division_statuses?.find(
                  ds => ds.division_id === viewingDivision.id
                );
                const currentStatus = divStatus?.status || period.status || 'pending';
                
                return (
                  <Card key={period.period_number} className={cn(
                    isDark ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))]" : "bg-[#F8F5F0] border-[#E0D4BF]"
                  )}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(currentStatus)}
                          <div>
                            <h3 className={cn("font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                              Period {period.period_number}
                            </h3>
                            <p className={cn("text-sm", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                              {period.period_title || (period.topics && period.topics.length > 0 && period.topics[0].topic)}
                            </p>
                            {viewingDivision && (
                              <p className={cn("text-xs mt-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>
                                Status for {viewingDivision.full_name}
                              </p>
                            )}
                          </div>
                        </div>
                        {viewingDivision && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant={currentStatus === 'completed' ? 'default' : 'outline'}
                              onClick={() => handleUpdatePeriodStatus(viewingPlan.id, period.period_number, 'completed', viewingDivision.id)}
                              disabled={updatingStatus}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Completed
                            </Button>
                            <Button
                              size="sm"
                              variant={currentStatus === 'partial' ? 'default' : 'outline'}
                              onClick={() => handleUpdatePeriodStatus(viewingPlan.id, period.period_number, 'partial', viewingDivision.id)}
                              disabled={updatingStatus}
                            >
                              <AlertCircle className="w-4 h-4 mr-1" />
                              Partial
                            </Button>
                            <Button
                              size="sm"
                              variant={currentStatus === 'skipped' ? 'default' : 'outline'}
                              onClick={() => handleUpdatePeriodStatus(viewingPlan.id, period.period_number, 'skipped', viewingDivision.id)}
                              disabled={updatingStatus}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Skipped
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

